import {
    ChannelConfig,
    ChannelType,
    MessageComposition,
    Template,
    TEMPLATE_TYPE_BY_CHANNEL,
    emptyChannelConfig,
    emptyEnvelope,
} from '../../../../../types/communication';
import { blockText, blocksToHtml, createBlock } from '../../../../../utils/emailHtml';
import { envelopeTexts, envelopeToJson } from '../../../../../utils/emailEnvelope';
import { extractVariableNames, syncVariables } from '../../../../../utils/templateVariables';
import { isSystemConstant } from '../../../../../utils/communicationConstants';
import { TemplateDraftInput } from '../../../../../services/communicationApi';

export const NEW_ID_PREFIX = 'draft-';

let draftSequence = 0;

export const draftId = (): string => {
    draftSequence += 1;
    return `${NEW_ID_PREFIX}${Date.now().toString(36)}-${draftSequence}`;
};

export const isDraftId = (id: string): boolean => id.startsWith(NEW_ID_PREFIX);

const nowIso = () => new Date().toISOString();

const SLACK_STARTER =
    ':bell: *Título do aviso*\n\n*Contrato:* {{contractNumber}}\n*Valor:* {{amount}}\n\n:link: <{{APP_BO_ADDRESS}}/caminho/{{id}}|Abrir no backoffice>';

const baseTemplate = (channel: ChannelType, name: string): Template => ({
    id: draftId(),
    name,
    description: '',
    channel,
    type: TEMPLATE_TYPE_BY_CHANNEL[channel],
    providerTemplateName: null,
    content: '',
    variables: [],
    messageCompositionId: null,
    status: 'ACTIVE',
    createdAt: nowIso(),
    updatedAt: nowIso(),
});

export const isEnvelope = (template: Template): boolean => template.role === 'ENVELOPE';

export const newEnvelopeDraft = (name: string): Template => {
    const envelope = emptyEnvelope();
    return {
        ...baseTemplate('EMAIL', name),
        role: 'ENVELOPE',
        type: 'JSON',
        envelope,
        content: envelopeToJson(envelope),
    };
};

export const newTemplateDraft = (channel: ChannelType, name: string): Template => {
    const base = baseTemplate(channel, name);
    if (channel === 'EMAIL') {
        const blocks = [createBlock('TEXT'), createBlock('FOOTER')];
        return { ...base, role: 'BODY', blocks, editorMode: 'BLOCKS', content: blocksToHtml(blocks) };
    }
    if (channel === 'INTERNAL_MESSAGE') return { ...base, content: SLACK_STARTER };
    return { ...base, providerBindings: [] };
};

export const newMetaDraft = (channel: ChannelType): MessageComposition => ({
    id: draftId(),
    name: '',
    description: '',
    channel,
    templateIds: [],
    templates: [],
    groupId: null,
    channelConfig: emptyChannelConfig(channel),
    raw: { id: '', type: channel === 'EMAIL' ? 'MAIL' : channel === 'INTERNAL_MESSAGE' ? 'SLACK' : 'WPP', templates: [] },
    status: 'ACTIVE',
    statusReason: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
});

export const cloneTemplateDraft = (source: Template, name: string): Template => ({
    ...source,
    id: draftId(),
    name,
    blocks: source.blocks?.map((b) => ({ ...b, id: `${b.id}-c${draftSequence}` })),
    envelope: source.envelope ? JSON.parse(JSON.stringify(source.envelope)) : undefined,
    variables: source.variables.map((v) => ({ ...v })),
    providerBindings: source.providerBindings?.map((b) => ({ ...b })),
    messageCompositionId: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
});

export const templateLabel = (template: Template): string => {
    if (isEnvelope(template)) return 'Envio (assunto e destinatários)';
    if (template.channel === 'EMAIL') return `Corpo do e-mail · ${template.name}`;
    return template.name;
};

export const envelopeOf = (templates: Template[]): Template | undefined => templates.find(isEnvelope);

export const bodiesOf = (templates: Template[]): Template[] => templates.filter((t) => !isEnvelope(t));

export const renderableContent = (template: Template): string => {
    if (isEnvelope(template) && template.envelope) return envelopeToJson(template.envelope);
    return template.editorMode === 'BLOCKS' && template.blocks ? blocksToHtml(template.blocks) : template.content;
};

export const templateInput = (template: Template): TemplateDraftInput => ({
    name: template.name,
    description: template.description,
    channel: template.channel,
    role: template.role,
    providerTemplateName: template.providerTemplateName,
    content: renderableContent(template),
    envelope: template.envelope,
    blocks: template.blocks,
    editorMode: template.editorMode,
    providerBindings: template.providerBindings,
    variables: template.variables,
});

const referencedNames = (template: Template, config: ChannelConfig): string[] => {
    if (isEnvelope(template)) return extractVariableNames(...envelopeTexts(template.envelope ?? emptyEnvelope()));
    if (template.channel === 'EXTERNAL_MESSAGE') {
        const recipients = config.kind === 'EXTERNAL_MESSAGE' ? config.to : [];
        const bound = (template.providerBindings ?? []).map((b) => b.variable).filter(Boolean);
        const names = [...extractVariableNames(...recipients), ...bound];
        return names.filter((n, i) => names.indexOf(n) === i);
    }
    if (template.editorMode === 'BLOCKS' && template.blocks) {
        return extractVariableNames(...template.blocks.map(blockText));
    }
    return extractVariableNames(template.content);
};

export const usedVariableNames = (template: Template, config: ChannelConfig): string[] =>
    referencedNames(template, config).filter((name) => !isSystemConstant(name));

export const usedConstantNames = (template: Template, config: ChannelConfig): string[] =>
    referencedNames(template, config).filter(isSystemConstant);

export const withSyncedVariables = (template: Template, config: ChannelConfig): Template => ({
    ...template,
    variables: syncVariables(
        template.variables.filter((v) => !isSystemConstant(v.name)),
        usedVariableNames(template, config),
    ),
    content: renderableContent(template),
});

export const syncAll = (templates: Template[], config: ChannelConfig): Template[] =>
    templates.map((t) => withSyncedVariables(t, config));

export const unusedVariableNames = (template: Template, config: ChannelConfig): string[] => {
    const used = usedVariableNames(template, config);
    return template.variables.filter((v) => !used.includes(v.name)).map((v) => v.name);
};

export const suggestTemplateName = (meta: MessageComposition, existing: Template[]): string => {
    const base = (meta.name || 'MODELO').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (!existing.length) return base;
    return `${base}_V${existing.length + 1}`;
};
